import { useState } from "react";
import { Link, useLocation } from "wouter";
import {
  useListContractTemplates,
  useListContracts,
  useDeleteContractTemplate,
  useCloneContractTemplate,
  getListContractTemplatesQueryKey,
  getListContractsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrg } from "@/lib/orgContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { Plus, Search, Copy, Pencil, Trash2, LayoutTemplate, Filter, Building2, Layers, Clock, FileText } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ROLE_LABELS: Record<string, string> = {
  head_coach: "Head Coach",
  assistant_coach: "Assistant Coach",
  coordinator: "Coordinator",
  director_of_ops: "Director of Operations",
  olympic_sport_head_coach: "Olympic Sport Head Coach",
};

const INSTITUTION_TYPE_LABELS: Record<string, string> = {
  public_d1: "Public D1",
  public_d2: "Public D2",
  public_d3: "Public D3",
  private_d1: "Private D1",
  private_d2: "Private D2",
  private_d3: "Private D3",
};

const ROLE_COLORS: Record<string, string> = {
  head_coach: "bg-blue-50 text-blue-700 border-blue-200",
  assistant_coach: "bg-green-50 text-green-700 border-green-200",
  coordinator: "bg-purple-50 text-purple-700 border-purple-200",
  director_of_ops: "bg-orange-50 text-orange-700 border-orange-200",
  olympic_sport_head_coach: "bg-teal-50 text-teal-700 border-teal-200",
};

export default function Templates() {
  const [, setLocation] = useLocation();
  const { org } = useOrg();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAdmin = org?.role === "admin";
  const isHr = org?.personas?.includes("HR") ?? false;
  const showRecentDrafts = isAdmin || isHr;

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [institutionTypeFilter, setInstitutionTypeFilter] = useState("all");
  // Document-type filter — analogous to the one on Contracts: lets HR/Legal
  // narrow templates down to either full-contract templates or
  // term-sheet templates. Filtered client-side; templates list is small.
  const [docKindFilter, setDocKindFilter] = useState<"all" | "contract" | "term_sheet">("all");

  const { data: rawTemplates = [], isLoading } = useListContractTemplates(
    {
      ...(roleFilter !== "all" ? { role: roleFilter } : {}),
      ...(institutionTypeFilter !== "all" ? { institutionType: institutionTypeFilter } : {}),
      ...(search ? { search } : {}),
    },
    { query: { queryKey: getListContractTemplatesQueryKey({ role: roleFilter !== "all" ? roleFilter : undefined, institutionType: institutionTypeFilter !== "all" ? institutionTypeFilter : undefined, search: search || undefined }) } },
  );

  const templates = rawTemplates.filter((t) => {
    if (docKindFilter === "all") return true;
    const k = (t as { documentKind?: string }).documentKind ?? "contract";
    return k === docKindFilter;
  });

  const deleteTemplate = useDeleteContractTemplate();
  const cloneTemplate = useCloneContractTemplate();

  const { data: recentDrafts = [] } = useListContracts(
    { status: "pending" as const, limit: 5 },
    { query: { queryKey: getListContractsQueryKey({ status: "pending", limit: 5 }), enabled: !!org && showRecentDrafts } },
  );

  const handleDelete = (id: number, name: string) => {
    deleteTemplate.mutate({ id }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListContractTemplatesQueryKey() });
        toast({ title: "Template deleted", description: `"${name}" has been removed.` });
      },
      onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not delete template" }),
    });
  };

  const handleClone = (id: number, name: string) => {
    cloneTemplate.mutate({ id }, {
      onSuccess: (cloned) => {
        queryClient.invalidateQueries({ queryKey: getListContractTemplatesQueryKey() });
        toast({ title: "Template cloned", description: `"${name}" copied as "${cloned.name}".` });
      },
      onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not clone template" }),
    });
  };

  const hasActiveFilters = roleFilter !== "all" || institutionTypeFilter !== "all" || docKindFilter !== "all" || !!search;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Contract Templates</h1>
          <p>Role-based templates to pre-fill contracts with standard clauses and default values.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setLocation("/templates/new")}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New Template
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates…"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-[200px]">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <Filter className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="head_coach">Head Coach</SelectItem>
                  <SelectItem value="assistant_coach">Assistant Coach</SelectItem>
                  <SelectItem value="coordinator">Coordinator</SelectItem>
                  <SelectItem value="director_of_ops">Director of Operations</SelectItem>
                  <SelectItem value="olympic_sport_head_coach">Olympic Sport Head Coach</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-[200px]">
              <Select value={institutionTypeFilter} onValueChange={setInstitutionTypeFilter}>
                <SelectTrigger>
                  <Building2 className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="All Division Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Division Types</SelectItem>
                  <SelectItem value="public_d1">Public D1</SelectItem>
                  <SelectItem value="public_d2">Public D2</SelectItem>
                  <SelectItem value="public_d3">Public D3</SelectItem>
                  <SelectItem value="private_d1">Private D1</SelectItem>
                  <SelectItem value="private_d2">Private D2</SelectItem>
                  <SelectItem value="private_d3">Private D3</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-[180px]">
              <Select value={docKindFilter} onValueChange={(v) => setDocKindFilter(v as "all" | "contract" | "term_sheet")}>
                <SelectTrigger data-testid="select-template-doc-kind">
                  <FileText className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="All Document Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Document Types</SelectItem>
                  <SelectItem value="contract">Contracts only</SelectItem>
                  <SelectItem value="term_sheet">Term sheets only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-44 rounded-xl skeleton-shimmer" />
              ))}
            </div>
          ) : templates.length === 0 ? (
            hasActiveFilters ? (
              <EmptyState
                icon={LayoutTemplate}
                title="No templates match your filters"
                description="Try adjusting your search or filter criteria."
                variant="compact"
              />
            ) : (
              <EmptyState
                icon={LayoutTemplate}
                title="No templates yet"
                description="Create role-based templates to start drafts 80% pre-filled with the right clauses and default values."
                action={isAdmin ? { label: "Create first template", href: "/templates/new" } : undefined}
              />
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger-children">
              {templates.map((template) => {
                const roleColor = ROLE_COLORS[template.role] ?? "bg-muted text-muted-foreground border-border";
                const roleLabel = ROLE_LABELS[template.role] ?? template.role;
                const institutionLabel = template.institutionType ? INSTITUTION_TYPE_LABELS[template.institutionType] : null;
                const clauseCount = template.clauseRefs?.length ?? 0;
                const defaultFields = template.defaultFields ?? {};

                return (
                  <div
                    key={template.id}
                    className="group relative rounded-xl border bg-card hover:bg-muted/20 hover:border-primary/30 hover:shadow-sm transition-all p-4 flex flex-col gap-3"
                  >
                    {template.isSeed && (
                      <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-gold/10 text-gold border border-gold/20">
                        Starter
                      </span>
                    )}
                    <div className="flex items-start gap-3 pr-12">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Layers className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{template.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {template.sport ? `${template.sport} · ` : ""}{institutionLabel ?? "Any Institution"}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${roleColor}`}>
                        {roleLabel}
                      </span>
                      {institutionLabel && (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 border-slate-200">
                          {institutionLabel}
                        </span>
                      )}
                      {(template as { documentKind?: string }).documentKind === "term_sheet" && (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200">
                          Term Sheet
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {(defaultFields as Record<string, unknown>).annualSalary != null && (
                        <div className="bg-muted/40 rounded-lg p-2">
                          <div className="text-muted-foreground">Base Salary</div>
                          <div className="font-semibold mt-0.5">${Number((defaultFields as Record<string, unknown>).annualSalary).toLocaleString()}</div>
                        </div>
                      )}
                      <div className="bg-muted/40 rounded-lg p-2">
                        <div className="text-muted-foreground">Clauses</div>
                        <div className="font-semibold mt-0.5">{clauseCount} attached</div>
                      </div>
                    </div>

                    {isAdmin && (
                      <div className="flex items-center gap-1.5 pt-1 border-t border-border">
                        <Button variant="ghost" size="sm" className="flex-1 h-7 text-xs" asChild>
                          <Link href={`/templates/${template.id}/edit`}>
                            <Pencil className="h-3 w-3 mr-1" />Edit
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => handleClone(template.id, template.name)}
                          disabled={cloneTemplate.isPending}
                          title="Clone template"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs px-2 text-destructive hover:text-destructive"
                              title="Delete template"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete template?</AlertDialogTitle>
                              <AlertDialogDescription>
                                "{template.name}" will be permanently deleted. Contracts already created from this template are not affected.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(template.id, template.name)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Drafts — shown for HR persona and admins; quick access to pending contracts */}
      {showRecentDrafts && (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Recent Drafts</CardTitle>
            </div>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setLocation("/contracts?status=pending")}>
              View all
            </Button>
          </div>
          <CardDescription>Pending contracts awaiting completion or signature.</CardDescription>
        </CardHeader>
        <CardContent>
          {recentDrafts.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No pending contracts right now.
            </div>
          ) : (
            <div className="divide-y rounded-lg border overflow-hidden">
              {recentDrafts.map((contract) => (
                <div
                  key={contract.id}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 cursor-pointer transition-colors"
                  onClick={() => setLocation(`/contracts/${contract.id}`)}
                >
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{contract.coachName}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {contract.title ?? contract.contractType} · {contract.sport}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs shrink-0 bg-amber-50 text-amber-700 border-amber-200">
                    Pending
                  </Badge>
                  <div className="text-xs text-muted-foreground shrink-0">
                    {new Date(contract.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
